import { useMemo, useState } from "react";
import dayjs from "@shared/dayjs";
import Avatar from "@shared/v2/Avatar";
import Checkbox from "@shared/v2/Checkbox";
import TextButton from "@shared/v2/TextButton";
import Typography from "@shared/v2/Typography";
import useSimpleNavigation from "@shared/v2/useSimpleNavigation";
import {
  ArrowsMaximizeSolidV6,
  ChevronRightSolidV6,
  MinimizeSolidV6,
  SpinnerSolidV6,
} from "@shared/v2/Icomoon";
import {
  DateRangeDropdown,
  resolveDateRange,
  type DateRange,
  type DateRangeOptionValue,
  type ReportDateConfig,
} from "@shared/v2/DateRangeDropdown";
import { Section } from "../BusinessDashboard";
import SalesFunnel from "./SalesFunnel";
import FunnelRing from "./FunnelRing";
import Percent from "./Percent";
import FunnelRingLabel from "./FunnelRingLabel";
import { CountsCallsTooltip, pendedTooltip, SetHeldTooltip, signedTooltip } from "./Tooltips";
import useSalesFunnel from "../BusinessDashboard/services/useSalesFunnel";
import IconButton from "@shared/v2/IconButton";
import useFullscreen from "../BusinessDashboard/hooks/useFullscreen";

// Sales Funnel is a future-inclusive report (scheduled appointments/closings),
// so the Custom picker allows future dates. Per-option truncation for
// this_week/this_month/this_quarter happens in `normalizeRangeForBackend` so
// their goal proration matches the pre-standardization behavior. `this_year`
// is left as Jan 1 – Dec 31 to hit the backend's full-annual-goal special case.
//
// Excluded options mirror the embedded SalesFunnel dropdown:
// - `this_week`/`last_week`: not in Listings::IndexQueryReact::VALID_DATE_RANGES,
//   so Pended/Closed ring drill-downs would open unfiltered listings.
// - `yesterday`/`this_quarter`/`last_quarter`: valid backend tokens on all rings,
//   but kept excluded to match the pre-Phase-2 dropdown until the full canonical
//   set is offered consistently across dashboards.
// Re-enable these once Listings::IndexQueryReact::VALID_DATE_RANGES and the
// close_date/status_changed scopes cover the missing week ranges.
const DATE_RANGE_CONFIG: ReportDateConfig = {
  disableFutureDates: false,
  defaultOption: "this_year",
  excludeOptions: ["yesterday", "this_week", "last_week", "this_quarter", "last_quarter"],
};

const PERIOD_TO_DATE_OPTIONS: ReadonlySet<DateRangeOptionValue> = new Set([
  "this_week",
  "this_month",
  "this_quarter",
]);

const normalizeRangeForBackend = (value: string, range: DateRange): DateRange => {
  if (!PERIOD_TO_DATE_OPTIONS.has(value as DateRangeOptionValue) || !range.endAt) return range;
  const today = dayjs().format("YYYY-MM-DD");
  return range.endAt > today ? { ...range, endAt: today } : range;
};

const DEFAULT_DATE_RANGE_VALUE: DateRangeOptionValue = "this_year";
const INITIAL_DATE_RANGE: DateRange = resolveDateRange(DEFAULT_DATE_RANGE_VALUE, {
  disableFutureDates: false,
});

type SortType = "calls" | "contacted" | "apptSet" | "apptHeld" | "apptSigned" | "pended" | "closed";
interface AgentData {
  userUuid: string;
  personUuid: string;
  name: string;
  avatarUrl: string;
  data: {
    calls: { actual: number; goal: number; onTarget: number };
    contacted: { actual: number; goal: number; onTarget: number };
    apptSet: { actual: number; goal: number; onTarget: number };
    apptHeld: { actual: number; goal: number; onTarget: number };
    apptSigned: { actual: number; goal: number; onTarget: number };
    pended: { actual: number; goal: number; onTarget: number };
    closed: { actual: number; goal: number; onTarget: number };
    callsContactedRatio: string;
    contactedApptSetRatio: string;
    apptSetApptHeldRatio: string;
    apptHeldApptSignedRatio: string;
    apptSignedPendedRatio: string;
    pendedClosedRatio: string;
  };
}

const SalesFunnelDashboard = () => {
  const { isFullscreen, ref, toggleFullscreen } = useFullscreen();
  const [sortBy, setSortBy] = useState<SortType>("calls");
  const [filters, setFilters] = useState<{
    buyer: boolean;
    seller: boolean;
    buyerSeller: boolean;
    na: boolean;
  }>({
    buyer: true,
    seller: true,
    buyerSeller: true,
    na: true,
  });
  const [dateRangeValue, setDateRangeValue] = useState<string>(DEFAULT_DATE_RANGE_VALUE);
  const [dateRange, setDateRange] = useState<DateRange>(INITIAL_DATE_RANGE);

  const handleDateRangeChange = (
    value: string,
    resolvedRange: DateRange,
    meta: { isValid: boolean },
  ) => {
    setDateRangeValue(value);
    // Skip partial Custom ranges: freezing on the last-valid range keeps the
    // report visible while the user finishes editing.
    if (!meta.isValid) return;
    setDateRange(normalizeRangeForBackend(value, resolvedRange));
  };

  const { push } = useSimpleNavigation();
  const {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    data,
    loading,
  }: {
    data: AgentData[];
    loading: boolean;
  } = useSalesFunnel({
    // @ts-expect-error props
    dateRange,
    filters,
    perAgent: true,
  });
  const sortedData = useMemo(
    () => data.sort((a, b) => b.data[sortBy].actual - a.data[sortBy].actual),
    [data, sortBy],
  );

  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  const updateFilter = (name: keyof typeof filters) => () =>
    setFilters((prev) => ({ ...prev, [name]: !prev[name] }));

  return (
    <div ref={ref} className="tw-bg-gray-5 tw-flex tw-p-[16px]">
      <div className="tw-max-w-[1500px] tw-w-full tw-mx-auto tw-flex tw-flex-col tw-gap-[16px]">
        {/* @ts-expect-error props */}
        <Section className="tw-flex tw-flex-col tw-gap-[16px]">
          <div className="tw-flex tw-justify-between">
            {/* @ts-expect-error props */}
            <Typography variant="headline1">Sales Funnel Dashboard</Typography>
            <div className="tw-flex tw-gap-[16px] tw-items-center">
              <DateRangeDropdown
                mode="standard"
                config={DATE_RANGE_CONFIG}
                onChange={handleDateRangeChange}
                dropdownClassName="tw-w-[200px]"
              />
              {/* @ts-expect-error props */}
              <IconButton schema="transparent" size="medium" onClick={toggleFullscreen}>
                {isFullscreen ? <MinimizeSolidV6 /> : <ArrowsMaximizeSolidV6 size="xl" />}
              </IconButton>
            </div>
          </div>

          <div className="tw-flex-none tw-border-[1px] tw-border-solid tw-border-gray-10" />

          <div className="tw-flex tw-justify-between">
            <div className="tw-flex tw-gap-[16px] tw-items-center">
              {/* @ts-expect-error props */}
              <Typography>Filter By:</Typography>
              {/* @ts-expect-error props */}
              <Checkbox label="Buyer" checked={filters.buyer} onChange={updateFilter("buyer")} />
              {/* @ts-expect-error props */}
              <Checkbox label="Seller" checked={filters.seller} onChange={updateFilter("seller")} />
              {/* @ts-expect-error props */}
              <Checkbox
                label="Buyer/Seller"
                checked={filters.buyerSeller}
                onChange={updateFilter("buyerSeller")}
              />
            </div>
            {/* @ts-expect-error props */}
            <TextButton
              className="tw-flex tw-gap-[4px] tw-items-center"
              size="medium"
              schema="sentence"
              onClick={() => window.location.assign("/settings/goals")}
            >
              Goal Settings <ChevronRightSolidV6 />
            </TextButton>
          </div>
        </Section>

        <SalesFunnel
          customTitle="Overview - Team Sales Funnel"
          customDateRange={dateRange}
          customDateToken={dateRangeValue}
          customFilters={filters}
          customAction={
            // @ts-expect-error props
            <TextButton
              className="tw-flex tw-gap-[4px] tw-items-center"
              size="medium"
              schema="sentence"
              onClick={() => window.location.assign("/agent_stats?report_tab=businessDashboard")}
            >
              Business Dashboard <ChevronRightSolidV6 />
            </TextButton>
          }
        />

        {/* @ts-expect-error props */}
        <Section className="tw-min-h-[200px]">
          <div className="tw-flex tw-flex-col tw-gap-[32px]">
            <div className="tw-flex tw-justify-between tw-items-center">
              {/* @ts-expect-error props */}
              <Typography variant="headline2" className="tw-font-bold">
                Agent Sales Funnel
              </Typography>
              <div className="tw-flex tw-gap-[8px] tw-items-center">
                Sort By:
                <select
                  className="tw-border-none tw-bg-white"
                  onChange={(e) => setSortBy(e.target.value as SortType)}
                  value={sortBy}
                >
                  <option value="calls">Called</option>
                  <option value="contacted">Contacted</option>
                  <option value="apptSet">Appt. Set</option>
                  <option value="apptHeld">Appt. Held</option>
                  <option value="apptSigned">Signed</option>
                  <option value="pended">Pended</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
            </div>

            <div className="tw-relative tw-flex tw-flex-col tw-overflow-x-auto">
              <div className="tw-flex">
                <FunnelRingLabel
                  title="Agent Name"
                  className="tw-text-center tw-w-[200px] tw-flex-none tw-mr-[64px]"
                />
                <div className="tw-min-w-[940px] tw-flex-1 tw-flex tw-justify-between">
                  <FunnelRingLabel
                    className="tw-text-center tw-min-w-[86px]"
                    title="Called"
                    tooltip={<CountsCallsTooltip />}
                  />
                  <FunnelRingLabel
                    className="tw-text-center tw-min-w-[86px]"
                    title="Contacted"
                    tooltip={<CountsCallsTooltip talked />}
                  />
                  <FunnelRingLabel
                    className="tw-text-center tw-min-w-[86px]"
                    title="Appt. Set"
                    tooltip={<SetHeldTooltip />}
                  />
                  <FunnelRingLabel
                    className="tw-text-center tw-min-w-[86px]"
                    title="Appt. Held"
                    tooltip={<SetHeldTooltip />}
                  />
                  <FunnelRingLabel
                    className="tw-text-center tw-min-w-[86px]"
                    title="Signed"
                    tooltip={signedTooltip}
                  />
                  <FunnelRingLabel
                    className="tw-text-center tw-min-w-[86px]"
                    title="Pended"
                    tooltip={pendedTooltip}
                  />
                  <FunnelRingLabel className="tw-text-center tw-min-w-[86px]" title="Closed" />
                </div>
              </div>
              {sortedData.map((agent) => (
                <div key={agent.userUuid} className="tw-flex tw-items-center tw-py-[24px] tw-gap-[64px]">
                  <div className="tw-flex tw-flex-col tw-gap-[8px] tw-w-[200px] tw-flex-none tw-justify-center tw-items-center">
                    {/* @ts-expect-error props */}
                    <Avatar src={agent.avatarUrl} alt="Test Agent" size="large" />
                    {/* @ts-expect-error props */}
                    <Typography variant="subtitle-m-bold" className="tw-text-center">
                      {agent.name}
                    </Typography>
                    <div className="tw-flex tw-gap-[4px] tw-items-center">
                      {/* @ts-expect-error props */}
                      <TextButton
                        schema="sentence"
                        onClick={() => window.open(`/settings/goals?user_uuid=${agent.userUuid}`, "_blank")}
                      >
                        Set Goals
                      </TextButton>
                      <div className="tw-self-stretch tw-border-solid tw-border-[1px] tw-border-gray-10 tw-min-h-[80%]" />
                      {/* @ts-expect-error props */}
                      <TextButton
                        schema="sentence"
                        onClick={() =>
                          push(
                            `?${new URLSearchParams({ report_tab: "agentDashboard", person_uuid: agent.personUuid })}`,
                          )
                        }
                      >
                        Dashboard
                      </TextButton>
                    </div>
                  </div>
                  <div className="tw-min-w-[940px] tw-pt-[24px] tw-flex-1 tw-flex tw-justify-between tw-items-stretch">
                    <FunnelRing
                      dateRange={dateRangeValue}
                      filters={filters}
                      size="small"
                      data={agent.data.calls}
                      showTitle={false}
                      title="Called"
                    />
                    <Percent
                      className="tw-self-stretch tw-mt-[-8px] tw-mb-[16px]"
                      size="small"
                      tooltip="of Called moved to Contacted"
                      value={`${agent.data.callsContactedRatio}%`}
                    />
                    <FunnelRing
                      dateRange={dateRangeValue}
                      filters={filters}
                      size="small"
                      data={agent.data.contacted}
                      showTitle={false}
                      title="Contacted"
                    />
                    <Percent
                      className="tw-self-stretch tw-mt-[-8px] tw-mb-[16px]"
                      size="small"
                      tooltip="of Contacted moved to Appt. Set"
                      value={`${agent.data.contactedApptSetRatio}%`}
                    />
                    <FunnelRing
                      dateRange={dateRangeValue}
                      filters={filters}
                      size="small"
                      data={agent.data.apptSet}
                      showTitle={false}
                      title="Appt. Set"
                    />
                    <Percent
                      className="tw-self-stretch tw-mt-[-8px] tw-mb-[16px]"
                      size="small"
                      tooltip="of Appt. Set moved to Appt. Held"
                      value={`${agent.data.apptSetApptHeldRatio}%`}
                    />
                    <FunnelRing
                      dateRange={dateRangeValue}
                      filters={filters}
                      size="small"
                      data={agent.data.apptHeld}
                      showTitle={false}
                      title="Appt. Held"
                    />
                    <Percent
                      className="tw-self-stretch tw-mt-[-8px] tw-mb-[16px]"
                      size="small"
                      tooltip="of Appt. Held moved to Signed"
                      value={`${agent.data.apptHeldApptSignedRatio}%`}
                    />
                    <FunnelRing
                      dateRange={dateRangeValue}
                      filters={filters}
                      size="small"
                      data={agent.data.apptSigned}
                      showTitle={false}
                      title="Appt. Signed"
                    />
                    <Percent
                      className="tw-self-stretch tw-mt-[-8px] tw-mb-[16px]"
                      size="small"
                      tooltip="of Signed moved to Pended"
                      value={`${agent.data.apptSignedPendedRatio}%`}
                    />
                    <FunnelRing
                      dateRange={dateRangeValue}
                      filters={filters}
                      size="small"
                      data={agent.data.pended}
                      showTitle={false}
                      title="Pended"
                    />
                    <Percent
                      className="tw-self-stretch tw-mt-[-8px] tw-mb-[16px]"
                      size="small"
                      tooltip="of Pended moved to Closed"
                      value={`${agent.data.pendedClosedRatio}%`}
                    />
                    <FunnelRing
                      dateRange={dateRangeValue}
                      filters={filters}
                      size="small"
                      data={agent.data.closed}
                      showTitle={false}
                      title="Closed"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
          {loading && (
            <div className="tw-rounded-[16px] tw-absolute tw-inset-0 tw-flex tw-justify-center tw-pt-[100px] tw-bg-white/50">
              <SpinnerSolidV6 className="tw-animate-spin" size="xxl" />
            </div>
          )}
        </Section>
      </div>
    </div>
  );
};

export default SalesFunnelDashboard;
